import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, object } from 'yup'

import {
  nonEmptyPositiveNumberSchema,
  nonEmptyStringSchema,
  useMetaResponseHandler,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { StoresContract } from '@/Store/application'
import { StoresRepository } from '@/Store/domain'
import { storesTranslation } from '@/Store/ui/storesTranslation'

export interface StoreStickersForm {
  merchantTargetId: number
  addressId: string | null
  materialQuantity: number | null
}

const schema = (
  t: TFunction<'translation', undefined>,
): ObjectSchema<StoreStickersForm> =>
  object({
    merchantTargetId: nonEmptyPositiveNumberSchema(t),
    addressId: nonEmptyStringSchema(t),
    materialQuantity: nonEmptyPositiveNumberSchema(t, undefined, true, 1),
  })

export interface IUseStoreStickersController {
  form: UseFormReturn<StoreStickersForm>
  save: (data: StoreStickersForm) => Promise<void>
}

export const useStoreStickersController = (
  repository: StoresRepository,
  id: number,
): IUseStoreStickersController => {
  const { t } = useTranslation()
  const { startLoading, stopLoading } = useLoader()

  const { handleMetaResponse } = useMetaResponseHandler()

  const form = useForm<StoreStickersForm>({
    resolver: yupResolver(schema(t)),
    mode: 'onChange',
    defaultValues: {
      merchantTargetId: id,
      addressId: null,
      materialQuantity: null,
    },
  })

  const save = async (data: StoreStickersForm): Promise<void> => {
    if (!id) return
    startLoading()

    const response = await StoresContract(repository).orderStoreStickers(data)

    stopLoading()

    const valid = handleMetaResponse(response?.meta, form, {
      notifySuccess: true,
      successMessage: t(storesTranslation.stickers.successOrder),
    })

    if (valid) {
      form.reset({
        ...form.getValues(),
        materialQuantity: null,
      })
    }
  }

  return {
    form,
    save,
  }
}
